import { useState, useEffect, useRef, useCallback } from "react";
import { Editor, BubbleMenu } from "@tiptap/react";
import { getMarkRange } from "@tiptap/core";
import { Type, Link2, ExternalLink, SquarePen, Trash2 } from "lucide-react";

const iconStyle = {
  color: "#888",
  flexShrink: 0,
} as const;

const inputStyle = {
  border: "none",
  outline: "none",
  fontSize: "13px",
  flex: 1,
  minWidth: 0,
  background: "transparent",
  color: "#333",
  padding: 0,
} as const;

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  borderBottom: "1px solid #eee",
} as const;

const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: 4,
  fontSize: "13px",
  fontWeight: 500,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  whiteSpace: "nowrap",
  color: "#555",
} as const;

export default ({ editor }: { editor: Editor }) => {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const urlInputRef = useRef<HTMLInputElement>(null);

  const getLinkInfo = useCallback(() => {
    const { state } = editor;
    const linkType = state.schema.marks.link;
    const href = editor.getAttributes("link").href || "";
    const range = getMarkRange(state.selection.$from, linkType);
    const linkText = range
      ? state.doc.textBetween(range.from, range.to, " ")
      : "";
    return { href, linkText, range };
  }, [editor]);

  const enterEditMode = useCallback(() => {
    const { href, linkText } = getLinkInfo();
    setUrl(href);
    setText(linkText);
    setMode("edit");
  }, [getLinkInfo]);

  useEffect(() => {
    if (mode === "edit") {
      requestAnimationFrame(() => urlInputRef.current?.focus());
    }
  }, [mode]);

  useEffect(() => {
    const dom = editor.view.dom;
    const handler = () => enterEditMode();
    dom.addEventListener("edit-link", handler);
    return () => dom.removeEventListener("edit-link", handler);
  }, [editor, enterEditMode]);

  const shouldShow = useCallback(() => {
    if (modeRef.current === "edit") return true;
    return editor.isActive("link");
  }, [editor]);

  const handleRemove = () => {
    setMode("view");
    requestAnimationFrame(() => {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    });
  };

  const handleApply = () => {
    const finalUrl = url.startsWith("http") ? url : `https://${url}`;
    const { range } = getLinkInfo();
    const linkContent = {
      type: "text" as const,
      text: text || finalUrl,
      marks: [{ type: "link", attrs: { href: finalUrl } }],
    };

    if (range) {
      editor
        .chain()
        .focus()
        .insertContentAt({ from: range.from, to: range.to }, linkContent)
        .run();
    } else {
      editor.chain().focus().insertContent(linkContent).run();
    }
    setMode("view");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMode("view");
      editor.commands.focus();
    }
  };

  const currentHref = editor.getAttributes("link").href || "";
  const strippedUrl = currentHref
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "");
  const displayUrl =
    strippedUrl.length > 45 ? strippedUrl.slice(0, 45) + "\u2026" : strippedUrl;

  if (mode === "edit") {
    return (
      <BubbleMenu
        editor={editor}
        shouldShow={shouldShow}
        tippyOptions={{
          interactive: true,
          placement: "bottom-start",
          appendTo: () => document.body,
          zIndex: 1300,
          onHidden: () => setMode("view"),
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            width: 320,
            overflow: "hidden",
          }}
        >
          <div style={rowStyle}>
            <Type size={14} style={iconStyle} />
            <input
              style={inputStyle}
              placeholder="Text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div style={{ ...rowStyle, borderBottom: "none" }}>
            <Link2 size={14} style={iconStyle} />
            <input
              ref={urlInputRef}
              style={inputStyle}
              placeholder="URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              style={{
                ...actionBtn,
                color: url.trim() ? "#198282" : "#bbb",
                cursor: url.trim() ? "pointer" : "default",
              }}
              onClick={handleApply}
              disabled={!url.trim()}
            >
              Apply
            </button>
          </div>
        </div>
      </BubbleMenu>
    );
  }

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      tippyOptions={{
        interactive: true,
        placement: "bottom-start",
        appendTo: () => document.body,
        zIndex: 1300,
      }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          padding: "4px 4px",
          gap: 2,
        }}
      >
        <a
          href={currentHref}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...actionBtn,
            color: "#198282",
            fontWeight: 600,
            textDecoration: "none",
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={currentHref}
        >
          <ExternalLink size={13} style={{ flexShrink: 0 }} />
          {displayUrl}
        </a>
        <div
          style={{
            width: 1,
            height: 16,
            background: "#ddd",
            flexShrink: 0,
          }}
        />
        <button
          style={actionBtn}
          onClick={enterEditMode}
          onMouseOver={(e) => (e.currentTarget.style.background = "#f5f5f5")}
          onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
          title="Edit link"
        >
          <SquarePen size={14} />
        </button>
        <div
          style={{
            width: 1,
            height: 16,
            background: "#ddd",
            flexShrink: 0,
          }}
        />
        <button
          style={{ ...actionBtn, color: "#c62828" }}
          onClick={handleRemove}
          onMouseOver={(e) => (e.currentTarget.style.background = "#fef2f2")}
          onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
          title="Remove link"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </BubbleMenu>
  );
};
